import React from 'react'

type Variant = 'primary' | 'secondary' | 'ghost'
type Size = 'md' | 'lg'

interface AppButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
}

const buttonClasses = (variant: Variant, size: Size, fullWidth: boolean): string => {
  const base = [
    'inline-flex items-center justify-center gap-2',
    'font-semibold',
    'transition-all duration-150',
    'active:scale-[0.98]',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent)]',
    'rounded-[var(--radius-button)]',
    fullWidth ? 'w-full' : '',
  ].join(' ')

  const sizing = size === 'lg' ? 'h-14 px-8 text-base' : 'h-11 px-5 text-sm'

  const variantClasses =
    variant === 'primary'
      ? 'text-[color:var(--accent-on)] hover:brightness-110'
      : variant === 'secondary'
        ? 'border border-[color:var(--accent)] text-[color:var(--accent)] hover:bg-[color:var(--accent-soft)]'
        : 'text-[color:var(--accent)] hover:bg-[color:var(--accent-soft)]'

  return [base, sizing, variantClasses].join(' ')
}

// For primary, the background is applied via inline style so the gradient theme works.
const buttonStyle = (variant: Variant): React.CSSProperties =>
  variant === 'primary'
    ? {
        background: 'var(--accent-gradient, var(--accent))',
        boxShadow: 'var(--shadow-button)',
      }
    : {}

/**
 * Themed button. Variants:
 *  - primary  : filled accent, white text. The default.
 *  - secondary: outline accent, accent text.
 *  - ghost    : transparent, accent text. Used for tertiary actions.
 */
export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(function AppButton(
  {
    variant = 'primary',
    size = 'lg',
    loading = false,
    fullWidth = false,
    disabled,
    className = '',
    children,
    ...rest
  },
  ref
) {
  const inlineStyle = buttonStyle(variant)

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[buttonClasses(variant, size, fullWidth), className].join(' ')}
      style={inlineStyle}
      {...rest}
    >
      {loading ? (
        <>
          <Spinner />
          <span>{typeof children === 'string' ? children : 'Loading…'}</span>
        </>
      ) : (
        children
      )}
    </button>
  )
})

interface AppLinkButtonProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: Variant
  size?: Size
  fullWidth?: boolean
}

/**
 * Anchor styled like AppButton, for navigations that must be real browser
 * navigations rather than SPA route changes — e.g. `target="_blank"` break-out
 * links that leave a partner iframe / in-app WebView for a top-level tab.
 */
export const AppLinkButton = React.forwardRef<HTMLAnchorElement, AppLinkButtonProps>(function AppLinkButton(
  { variant = 'primary', size = 'lg', fullWidth = false, className = '', children, ...rest },
  ref
) {
  return (
    <a
      ref={ref}
      className={[buttonClasses(variant, size, fullWidth), className].join(' ')}
      style={buttonStyle(variant)}
      {...rest}
    >
      {children}
    </a>
  )
})

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

export default AppButton
